/* eslint-disable react-refresh/only-export-components */
/**
 * Reusable Premium Design System Components for the Dashboard.
 * These components ensure consistent styling, spacing, and depth
 * across all dashboard modules.
 */

import { Children } from 'react';
import { Input } from '@/components/Elements/Input';

export function DashboardPage({ children, className = '' }) {
  return (
    <div
      id="dashboard-scroll-container"
      className={`w-full flex flex-col rounded-[16px] p-4 font-sf-pro-display text-text-primary animate-fadeIn overflow-y-auto overflow-x-hidden custom-scrollbar h-full min-h-0 flex-1 bg-layer-1-page ${className}`}
    >
      {children}
    </div>
  );
}

export function DashboardHeader({ title, children, className = '' }) {
  return (
    <div
      className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-4 ${className}`}
    >
      <h1 className="text-xl sm:text-h3 font-semibold text-text-primary tracking-wide">
        {title}
      </h1>
      <div className="flex flex-wrap items-center gap-3">{children}</div>
    </div>
  );
}

/** Pill-style tab switcher — pairs with a section title in the same row. */
export function PillTabs({ items = [], value, onChange, className = '' }) {
  return (
    <div
      className={`flex items-center gap-1 sm:gap-2 min-w-0 max-w-full overflow-x-auto no-scrollbar ${className}`}
      role="tablist"
    >
      {items.map((item) => {
        const active = value === item.id;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange?.(item.id)}
            className={`h-8 sm:h-9 px-2 sm:px-4 flex-1 sm:flex-none flex items-center justify-center whitespace-nowrap rounded-lg border text-[11px] sm:text-sm font-medium transition-colors ${
              active
                ? 'bg-surface-mint border-primary text-primary'
                : 'bg-form-background-light border-transparent text-text-secondary hover:text-text-primary'
            }`}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

export function DashboardSection({
  title,
  titleClass = 'text-h6',
  children,
  actions,
  largeGap = false,
  gapClassName,
  className = '',
}) {
  const gapClass = gapClassName || (largeGap ? 'gap-8' : 'gap-6');

  return (
    <div className={`flex flex-col w-full ${gapClass} ${className}`}>
      {(title || actions) && (
        <div className="flex items-center justify-between">
          {title && (
            <h2
              className={`${titleClass} font-bold text-text-primary tracking-wide`}
            >
              {title}
            </h2>
          )}
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </div>
      )}
      {children}
    </div>
  );
}

export function DashboardCard({
  children,
  className = '',
  noPadding = false,
  elevated = false,
  hover = false,
  onClick,
}) {
  const surfaceClass = elevated
    ? 'premium-surface-elevated bg-layer-6-elevated'
    : 'premium-surface bg-layer-5-card';
  const hoverClass = hover
    ? 'premium-surface-hover hover:bg-layer-7-hover transition-all duration-300'
    : '';
  const paddingClass = noPadding ? '' : 'p-4 sm:p-6 laptop:!p-4';

  return (
    <div
      onClick={onClick}
      className={`rounded-2xl ${surfaceClass} ${hoverClass} ${paddingClass} ${className}`}
    >
      {children}
    </div>
  );
}

export function DashboardCardHeader({
  title,
  description,
  actions,
  border = true,
}) {
  return (
    <div
      className={`flex flex-col sm:flex-row sm:items-center justify-between gap-4 laptop:!gap-2 pb-4 laptop:!pb-3 ${border ? 'border-b border-border-default mb-5 laptop:!mb-3' : ''}`}
    >
      <div className="flex flex-col gap-1 text-left">
        {title && (
          <h3 className="text-body font-bold text-text-primary">{title}</h3>
        )}
        {description && (
          <p className="text-caption text-text-secondary">{description}</p>
        )}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function DashboardMetricCard({
  title,
  value,
  icon: Icon,
  iconSrc,
  iconBg,
  accent,
  onClick,
  compact = false,
}) {
  const containerClasses = compact
    ? 'min-h-[64px] gap-2 py-2.5 pl-3.5 pr-2.5'
    : 'min-h-[64px] xl:min-h-[88px] laptop:!min-h-[84px] laptop:!py-2 laptop:!gap-2 gap-2 xl:gap-3 py-2.5 xl:py-3 pl-3.5 xl:px-4 pr-2.5';

  const barClasses = compact
    ? 'bottom-2.5 top-2.5'
    : 'bottom-2.5 xl:bottom-3 top-2.5 xl:top-3';

  const iconWrapperClasses = compact
    ? 'h-8 w-8'
    : 'h-8 w-8 xl:h-12 xl:w-12 laptop:!h-9 laptop:!w-9';

  const iconImgClasses = compact ? 'h-4 w-4' : 'h-4 w-4 xl:h-6 xl:w-6';

  const titleClasses = compact ? 'text-xs' : 'text-xs xl:text-[15px]';

  const valueClasses = compact ? 'text-lg' : 'text-lg xl:text-2xl';

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={`relative flex w-full flex-row items-center overflow-hidden rounded-[12px] bg-white text-left shadow-[0_1px_3px_rgba(0,0,0,0.06)] transition-colors disabled:pointer-events-none ${containerClasses}`}
    >
      <span
        className={`absolute left-0 w-[4px] rounded-r-full ${barClasses}`}
        style={{ backgroundColor: accent }}
      />
      <span
        className={`flex shrink-0 items-center justify-center rounded-[10px] ${iconWrapperClasses} ${iconBg}`}
      >
        {iconSrc ? (
          <img
            src={iconSrc}
            alt=""
            className={`object-contain ${iconImgClasses}`}
          />
        ) : (
          <Icon className={iconImgClasses} />
        )}
      </span>
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className={`truncate text-text-secondary ${titleClasses}`}>
          {title}
        </span>
        <span
          className={`font-bold leading-6 text-text-primary ${valueClasses}`}
        >
          {value}
        </span>
      </span>
    </button>
  );
}

export function DashboardTableWrapper({ children, className = '' }) {
  return (
    // DashboardTableWrapper is a thin pass-through – DashboardTable already
    // handles overflow-x internally. Keep this wrapper for legacy usage.
    <div className={`w-full ${className}`}>{children}</div>
  );
}

/** Shared data table: mint border box + horizontal row separators (via .dashboard-data-table).
 *
 * Scroll architecture:
 *  - Horizontal scroll  → this container (overflow-x-auto)
 *  - Vertical scroll    → DashboardPage (the page-level scroll container)
 *  - No overflow-y here so `position:sticky` on thead works correctly.
 */
export function DashboardTable({
  children,
  className = '',
  containerClassName = '',
}) {
  return (
    <div
      className={`w-full overflow-x-auto overflow-y-visible custom-scrollbar rounded-[12px] bg-white ${containerClassName}`}
      style={{ WebkitOverflowScrolling: 'touch' }}
    >
      <table
        className={`dashboard-data-table w-full text-left border-separate border-spacing-0 whitespace-nowrap ${className}`}
      >
        {children}
      </table>
    </div>
  );
}

export function DashboardTableHeader({ children }) {
  const colCount = Children.toArray(children).filter(Boolean).length || 1;

  return (
    <thead className="sticky top-0 z-10 bg-[#1A241B]">
      <tr className="h-[48px] sm:h-[54px] laptop:!h-11 bg-[#1A241B] text-[#EBF4EA] text-left text-[15px] sm:text-[16px] font-medium tracking-normal [&_th]:bg-[#1A241B] [&_th]:font-medium [&_th]:text-[15px] sm:[&_th]:text-[16px] [&_th]:border-0 [&_th]:!rounded-none [&_th:first-child]:!rounded-l-[12px] [&_th:last-child]:!rounded-r-[12px]">
        {children}
      </tr>
      <tr aria-hidden="true" className="pointer-events-none bg-white">
        <th
          colSpan={colCount}
          className="!h-2 !p-0 !border-0 !bg-white !rounded-none"
        />
      </tr>
    </thead>
  );
}

export function DashboardTableBody({ children }) {
  return (
    <tbody className="text-xs sm:text-base text-[#1F2241] font-normal text-left bg-white">
      {children}
    </tbody>
  );
}

export function DashboardTableRow({
  children,
  selected = false,
  onClick,
  className = '',
}) {
  return (
    <tr
      onClick={onClick}
      className={`h-[40px] sm:h-[56px] laptop:!h-12 cursor-pointer premium-table-row hover:bg-layer-7-hover [&>td:first-child]:rounded-l-[12px] [&>td:last-child]:rounded-r-[12px] ${selected ? 'selected' : ''} ${className}`}
    >
      {children}
    </tr>
  );
}

/** Shared data-cell text style: 16px medium #1F2241 — keep every dashboard table's values in sync. */
export function DashboardTableCell({
  children,
  align = 'left',
  className = '',
  ...props
}) {
  const alignClass =
    align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : '';

  return (
    <td
      className={`py-2.5 sm:py-4 laptop:!py-2 px-3 sm:px-4 laptop:!px-3 text-sm sm:text-base font-normal text-[#1F2241] ${alignClass} ${className}`}
      {...props}
    >
      {children}
    </td>
  );
}

export {
  TableChip,
  TABLE_CHIP_BASE,
  TABLE_CHIP_TONES,
  getConsentChipTone,
  getStageChipTone,
} from '@/components/Elements/TableChip';

export function DashboardFilterBar({ children, className = '' }) {
  return (
    <div
      className={`flex items-center justify-between gap-3 flex-wrap mb-4 ${className}`}
    >
      {children}
    </div>
  );
}

export function DashboardInput({
  className = '',
  autoComplete = 'off',
  ...props
}) {
  return (
    <Input
      size="md"
      autoComplete={autoComplete}
      className={className}
      {...props}
    />
  );
}

export function DashboardChartCard({ title, children }) {
  return (
    <DashboardCard elevated className="flex flex-col gap-4">
      <h3 className="text-body font-bold text-text-primary mb-2">{title}</h3>
      <div className="flex-grow">{children}</div>
    </DashboardCard>
  );
}
